import React, { useState, useEffect } from 'react';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';
import syncService from '../services/syncService';
import pendingPhotosService from '../services/pendingPhotosService';
import apiService from '../services/apiService';
import CertificatInspection from './CertificatInspection';

const styles = {
  container: { minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', paddingBottom: '100px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: 'white', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 100 },
  backBtn: { background: 'none', border: 'none', fontSize: '16px', color: '#3b82f6', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px' },
  content: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '20px' },
  card: { background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' },
  cardHeaderViolet: { padding: '16px 20px', background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white' },
  cardHeaderOrange: { padding: '16px 20px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' },
  cardHeaderGreen: { padding: '16px 20px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' },
  cardBody: { padding: '16px' },
  infoRow: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', marginBottom: '8px' },
  infoIcon: { fontSize: '20px', flexShrink: 0 },
  infoContent: { flex: 1 },
  infoLabel: { fontSize: '12px', color: '#64748b', marginBottom: '2px' },
  infoValue: { fontSize: '14px', fontWeight: 600, color: '#1e293b' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  statCard: { padding: '16px', borderRadius: '12px', textAlign: 'center' },
  statNumber: { fontSize: '28px', fontWeight: 700, marginBottom: '4px' },
  statLabel: { fontSize: '12px', fontWeight: 500 },
  btnCertificat: { width: '100%', padding: '18px 24px', borderRadius: '12px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', border: 'none', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  actions: { position: 'fixed', bottom: 0, left: 0, right: 0, padding: '16px', background: 'white', borderTop: '1px solid #e2e8f0', boxShadow: '0 -4px 12px rgba(0,0,0,0.1)' },
  btnFinish: { width: '100%', padding: '18px 24px', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  syncStatus: { textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#64748b' }
};

function Etape13({ mission, onBack, onFinish }) {
  const [observationsCount, setObservationsCount] = useState({ total: 0, nc: 0, obs: 0 });
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  const [showCertificat, setShowCertificat] = useState(false);
  const [missionData, setMissionData] = useState(mission);
  const [pendingPhotosCount, setPendingPhotosCount] = useState(0);
  const [pendingWarning, setPendingWarning] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      // Charger toutes les étapes pour enrichir les données mission
      const etape1 = await inspectionStorage.getStep(mission.id, 'etape_1_infos_generales');
      const etape8 = await inspectionStorage.getStep(mission.id, 'etape_8_sources_energie');
      const etape11 = await inspectionStorage.getStep(mission.id, 'etape_11_observations');

      // Compter les observations
      if (etape11?.observations) {
        const observations = etape11.observations;
        const nc = observations.filter(o => o.gravite === 'non_conformite').length;
        const obs = observations.filter(o => o.gravite !== 'non_conformite').length;
        setObservationsCount({ total: observations.length, nc, obs });
      }

      // Enrichir les données mission pour le certificat
      setMissionData(prev => ({
        ...prev,
        etape_1_infos_generales: etape1 || {},
        etape_8_sources_energie: etape8 || {},
        etape_11_observations: etape11 || {}
      }));

      // Compter les photos prises mais non rattachées
      try {
        const count = await pendingPhotosService.count(mission.id);
        setPendingPhotosCount(count);
      } catch (e) {
        console.warn('Pending photos count error:', e?.message || e);
        setPendingPhotosCount(0);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const handleFinishClicked = async () => {
    // Toujours rafraîchir le compteur juste avant l'avertissement
    try {
      const count = await pendingPhotosService.count(mission.id);
      setPendingPhotosCount(count);
      if (count > 0) {
        setPendingWarning(true);
        return;
      }
    } catch (e) {
      console.warn('Pending photos count error:', e?.message || e);
    }
    await handleFinishAndSync();
  };

  const handleFinishAndSync = async () => {
    setPendingWarning(false);
    setIsSyncing(true);
    setSyncStatus('Synchronisation avec le serveur...');

    try {
      const uploadResult = await syncService.uploadMission(mission.id);
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Échec de la synchronisation');
      }

      await syncManager.syncStep(mission.id, 'statut_mobile', { statut: 'completee' });

      // ─── Transfert du verrou PWA → Web ──────────────────────────────────
      // À ce stade, la saisie est terminée et tout est synchronisé.
      // On rebascule le verrou côté bureau pour permettre la finalisation web
      // (signature, génération PDF, envoi, etc.). Le rapport est ensuite en
      // lecture seule depuis la PWA.
      setSyncStatus('🔐 Transfert du verrou vers le bureau...');
      try {
        const detailsRes = await apiService.getMissionDetails(mission.id);
        const rapportId = detailsRes?.data?.rapport?.id;
        if (rapportId) {
          const lockRes = await apiService.lockRapport(rapportId, 'web');
          if (!lockRes.success) {
            console.warn('⚠️ Transfert du verrou échoué:', lockRes.error);
            setSyncStatus(`⚠️ Sync OK mais transfert verrou échoué : ${lockRes.error}`);
          } else {
            setSyncStatus('✅ Rapport synchronisé et verrouillé pour le bureau');
          }
        } else {
          console.warn('⚠️ Pas d\'id rapport pour transférer le verrou');
          setSyncStatus('✅ Synchronisation réussie (pas de verrou à transférer)');
        }
      } catch (lockErr) {
        console.warn('⚠️ Erreur transfert verrou:', lockErr);
        setSyncStatus('✅ Synchronisation réussie (verrou non transféré)');
      }

      setTimeout(() => {
        if (onFinish) {
          onFinish();
        } else {
          onBack();
        }
      }, 1800);

    } catch (error) {
      console.error('Erreur sync:', error);
      setSyncStatus('⚠️ Erreur - Les données sont sauvegardées localement');

      setTimeout(() => {
        if (onFinish) onFinish();
      }, 2000);
    } finally {
      setIsSyncing(false);
    }
  };

  const dateVerification = mission.date_verification 
    ? new Date(mission.date_verification).toLocaleDateString('fr-FR')
    : '-';

  // Afficher le certificat en plein écran
  if (showCertificat) {
    return (
      <CertificatInspection 
        mission={missionData} 
        onClose={() => setShowCertificat(false)} 
      />
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>← Retour</button>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Étape 13/14</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>Finalisation</p>
        </div>
        <div style={{ width: '60px' }}></div>
      </div>

      <div style={styles.content}>
        {/* Résumé intervention */}
        <div style={styles.card}>
          <div style={styles.cardHeaderViolet}>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>📋 Résumé de l'intervention</h2>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.infoRow}>
              <span style={styles.infoIcon}>🏢</span>
              <div style={styles.infoContent}>
                <div style={styles.infoLabel}>Client</div>
                <div style={styles.infoValue}>{mission.client_nom || 'Non spécifié'}</div>
              </div>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoIcon}>📍</span>
              <div style={styles.infoContent}>
                <div style={styles.infoLabel}>Site</div>
                <div style={styles.infoValue}>{mission.etablissement || mission.site_adresse || 'Non spécifié'}</div>
              </div>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoIcon}>📅</span>
              <div style={styles.infoContent}>
                <div style={styles.infoLabel}>Date de vérification</div>
                <div style={styles.infoValue}>{dateVerification}</div>
              </div>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoIcon}>📄</span>
              <div style={styles.infoContent}>
                <div style={styles.infoLabel}>N° Rapport</div>
                <div style={styles.infoValue}>{mission.numero_rapport || 'BROUILLON'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques observations */}
        <div style={styles.card}>
          <div style={styles.cardHeaderOrange}>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>⚠️ Observations relevées</h2>
          </div>
          <div style={styles.cardBody}>
            <div style={styles.statsGrid}>
              <div style={{ ...styles.statCard, background: '#fee2e2' }}>
                <div style={{ ...styles.statNumber, color: '#dc2626' }}>{observationsCount.nc}</div>
                <div style={{ ...styles.statLabel, color: '#991b1b' }}>Non-conformités</div>
              </div>
              <div style={{ ...styles.statCard, background: '#fef3c7' }}>
                <div style={{ ...styles.statNumber, color: '#d97706' }}>{observationsCount.obs}</div>
                <div style={{ ...styles.statLabel, color: '#92400e' }}>Observations</div>
              </div>
              <div style={{ ...styles.statCard, background: '#dbeafe' }}>
                <div style={{ ...styles.statNumber, color: '#1e40af' }}>{observationsCount.total}</div>
                <div style={{ ...styles.statLabel, color: '#1e3a8a' }}>Total</div>
              </div>
            </div>
          </div>
        </div>

        {/* Certificat d'inspection */}
        <div style={styles.card}>
          <div style={styles.cardHeaderGreen}>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>📄 Certificat d'Inspection</h2>
          </div>
          <div style={styles.cardBody}>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px', lineHeight: 1.5 }}>
              Générez un certificat provisoire à remettre au client. Ce document récapitule les informations principales et les observations relevées.
            </p>
            <button style={styles.btnCertificat} onClick={() => setShowCertificat(true)}>
              <span style={{ fontSize: '20px' }}>📋</span>
              Prévisualiser le Certificat
            </button>
          </div>
        </div>
      </div>

      {/* Bannière "photos en attente" (informative) */}
      {pendingPhotosCount > 0 && (
        <div style={{
          margin: '0 16px',
          padding: '12px 16px',
          background: '#fffbeb',
          border: '1px solid #fcd34d',
          borderLeft: '6px solid #f59e0b',
          borderRadius: '10px',
          color: '#92400e',
          fontSize: '14px',
          fontWeight: 600,
        }}>
          📷 {pendingPhotosCount} photo(s) en attente d'observation — Vous pouvez
          les rattacher depuis l'étape 11 avant de valider.
        </div>
      )}

      {/* Action finale */}
      <div style={styles.actions}>
        <button 
          style={{ ...styles.btnFinish, ...(isSyncing ? styles.btnDisabled : {}) }}
          onClick={handleFinishClicked}
          disabled={isSyncing}
        >
          {isSyncing ? (
            <>
              <span>⏳</span>
              Synchronisation...
            </>
          ) : (
            <>
              <span>🔐</span>
              Verrouiller le rapport (transférer au bureau)
            </>
          )}
        </button>
        {syncStatus && (
          <div style={styles.syncStatus}>{syncStatus}</div>
        )}
      </div>

      {/* Modale d'avertissement : photos en attente non rattachées */}
      {pendingWarning && (
        <div
          onClick={() => setPendingWarning(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '16px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '16px', padding: '24px',
              maxWidth: '480px', width: '100%',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)'
            }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#92400e' }}>
              📷 {pendingPhotosCount} photo(s) sans observation
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#475569', lineHeight: 1.5 }}>
              Vous avez encore des photos qui n'ont pas été rattachées à une observation.
              Elles resteront enregistrées mais ne seront pas associées à l'inspection.
            </p>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#64748b' }}>
              👉 Retournez à l'étape 11 pour les rattacher, ou validez quand même
              si vous souhaitez les ignorer.
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setPendingWarning(false)}
                style={{
                  padding: '10px 16px', borderRadius: '10px', border: '1px solid #cbd5e1',
                  background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => { setPendingWarning(false); onBack(); }}
                style={{
                  padding: '10px 16px', borderRadius: '10px', border: 'none',
                  background: '#f59e0b', color: '#fff', fontWeight: 700, cursor: 'pointer'
                }}
              >
                ← Retour pour rattacher
              </button>
              <button
                type="button"
                onClick={handleFinishAndSync}
                style={{
                  padding: '10px 16px', borderRadius: '10px', border: 'none',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff', fontWeight: 700, cursor: 'pointer'
                }}
              >
                Valider quand même
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Etape13;